import React from 'react';
import { FunnelIcon, ListBulletIcon, Squares2X2Icon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

export type ActivityStatusFilter = 'active' | 'all' | 'inactive' | 'away';

import { Collaborator } from '@/features/Collaborators/Models/collaborator';

interface CollaboratorFiltersProps {
    nameSearchQuery: string;
    onNameSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onOpenAdvancedFilters: () => void;
    onOpenColumnSelector?: () => void;
    itemsPerPage?: number;
    onItemsPerPageChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    viewMode?: 'table' | 'card';
    onViewModeChange?: (mode: 'table' | 'card') => void;
    activityStatusFilter?: ActivityStatusFilter;
    onActivityStatusFilterChange?: (filter: ActivityStatusFilter) => void;
    collaborators?: Collaborator[];
    showAdvancedFilters?: boolean;
    showColumnSelector?: boolean;
    showStatusFilter?: boolean;
}

const CollaboratorFilters: React.FC<CollaboratorFiltersProps> = ({
    nameSearchQuery,
    onNameSearchChange,
    onOpenAdvancedFilters,
    onOpenColumnSelector,
    itemsPerPage,
    onItemsPerPageChange,
    viewMode,
    onViewModeChange,
    activityStatusFilter = 'active',
    onActivityStatusFilterChange,
    collaborators = [],
    showAdvancedFilters = true,
    showColumnSelector = true,
    showStatusFilter = true,
}) => {
    const getStatusFilterLabel = (filter: ActivityStatusFilter) => {
        switch (filter) {
            case 'active':
                return 'Colaboradores Ativos';
            case 'all':
                return 'Todos';
            case 'inactive':
                return 'Ex-Colaboradores';
            case 'away':
                return 'Afastados';
        }
    };

    const getStatusFilterColor = (filter: ActivityStatusFilter) => {
        switch (filter) {
            case 'active':
                return 'bg-green-100 text-green-700 border-green-200';
            case 'all':
                return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'inactive':
                return 'bg-red-100 text-red-700 border-red-200';
            case 'away':
                return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        }
    };

    const getStatusCount = (filter: ActivityStatusFilter): number => {
        if (filter === 'all') {
            return collaborators.length;
        }
        
        return collaborators.filter(collaborator => {
            const status = collaborator.status?.toLowerCase();
            
            switch (filter) {
                case 'active':
                    return status === 'active' || status === 'ativo';
                case 'inactive':
                    return status === 'inactive' || status === 'inativo' || status === 'desligado';
                case 'away':
                    return status === 'afastado';
                default:
                    return false;
            }
        }).length;
    };

    const handleStatusFilterClick = () => {
        if (!onActivityStatusFilterChange) return;
        
        const nextFilter: ActivityStatusFilter = 
            activityStatusFilter === 'active' ? 'all' :
            activityStatusFilter === 'all' ? 'inactive' :
            activityStatusFilter === 'inactive' ? 'away' : 'active';
        
        onActivityStatusFilterChange(nextFilter);
    };
    // Calcular número de colunas dinamicamente
    const visibleFilters = [
        true, // nome sempre visível
        showStatusFilter && onActivityStatusFilterChange,
        showAdvancedFilters,
        showColumnSelector && onOpenColumnSelector
    ].filter(Boolean).length;
    
    // Ajustar grid para acomodar melhor os filtros
    const gridCols = visibleFilters === 1 ? 'md:grid-cols-1' : 
                     visibleFilters === 2 ? 'md:grid-cols-2' :
                     visibleFilters === 3 ? 'md:grid-cols-3' :
                     visibleFilters === 4 ? 'md:grid-cols-4' : 'md:grid-cols-5';

    return (
        <div className="mb-4 p-4 bg-gray-50 shadow sm:rounded-lg flex-1">
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4 items-end`}>
                {/* Campo de Nome - largura normal quando há 4 filtros, dupla em outros casos */}
                <div className={visibleFilters === 1 ? "md:col-span-1" : visibleFilters === 4 ? "md:col-span-1" : "md:col-span-2"}>
                    <label htmlFor="nameSearch" className="block text-sm font-medium text-gray-700">
                        Filtrar por Nome
                    </label>
                    <input
                        type="text"
                        id="nameSearch"
                        className="input-primary mt-1 w-full"
                        value={nameSearchQuery}
                        onChange={onNameSearchChange}
                        placeholder="Digite o nome..."
                    />
                </div>
                
                {/* Filtro de Status */}
                {showStatusFilter && onActivityStatusFilterChange && (
                    <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </label>
                        <button
                            type="button"
                            onClick={handleStatusFilterClick}
                            className={`w-full px-3 py-2 text-sm font-medium rounded-md border transition-colors hover:opacity-80 mt-1 sm:mt-0 ${getStatusFilterColor(activityStatusFilter)} flex items-center justify-between`}
                        >
                            <span className="truncate">{getStatusFilterLabel(activityStatusFilter)}</span>
                            <span className="ml-2 inline-flex items-center justify-center px-2 py-0 text-xs font-bold rounded-full bg-white bg-opacity-20">
                                {getStatusCount(activityStatusFilter)}
                            </span>
                        </button>
                    </div>
                )}
                
                {/* Filtros Avançados */}
                {showAdvancedFilters && (
                    <div className="md:col-span-1">
                        <button
                            type="button"
                            onClick={onOpenAdvancedFilters}
                            className="btn-secondary w-full flex items-center justify-center space-x-2 mt-1 sm:mt-0"
                        >
                            <FunnelIcon className="w-5 h-5" />
                            <span>Filtros Avançados</span>
                        </button>
                    </div>
                )}
                
                {/* Seletor de Colunas */}
                {showColumnSelector && onOpenColumnSelector && (
                    <div className="md:col-span-1">
                        <button
                            type="button"
                            onClick={onOpenColumnSelector}
                            className="btn-secondary w-full flex items-center justify-center space-x-2 mt-1 sm:mt-0"
                        >
                            <AdjustmentsHorizontalIcon className="w-5 h-5" />
                            <span>Colunas</span>
                        </button>
                    </div>
                )}
                {itemsPerPage && onItemsPerPageChange && (
                    <div className="md:col-span-1">
                        <label htmlFor="itemsPerPage" className="block text-sm font-medium text-gray-700">
                            Itens por página
                        </label>
                        <select
                            id="itemsPerPage"
                            value={itemsPerPage}
                            onChange={onItemsPerPageChange}
                            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        >
                            <option>10</option>
                            <option>20</option>
                            <option>50</option>
                            <option>100</option>
                        </select>
                    </div>
                )}
                {viewMode && onViewModeChange && (
                    <div className="hidden md:flex justify-start md:justify-end md:col-span-1">
                        <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-2">Visualizar como:</span>
                            <div className="flex rounded-md shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => onViewModeChange('table')}
                                    className={`px-3 py-2 rounded-l-md text-sm font-medium focus:outline-none ${viewMode === 'table' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <ListBulletIcon className="h-5 w-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onViewModeChange('card')}
                                    className={`-ml-px px-3 py-2 rounded-r-md text-sm font-medium focus:outline-none ${viewMode === 'card' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <Squares2X2Icon className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CollaboratorFilters;